import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  BarChart3, 
  Settings, 
  Shield, 
  Home,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Mail
} from 'lucide-react';
import './AdminPanel.css';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is admin
    checkAdminAccess();
    loadData();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 403) {
        setError('Access Denied: Admin privileges required');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to verify admin access');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      // Load stats
      const statsResponse = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.stats);
      }

      // Load users
      const usersResponse = await fetch('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users || []);
      }

      // Load contact messages
      const messagesResponse = await fetch('/api/admin/contact-messages', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (messagesResponse.ok) {
        const messagesData = await messagesResponse.json();
        setContactMessages(messagesData.messages || []);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        await loadData(); // Reload data
        alert('User updated successfully');
      } else {
        const error = await response.json();
        alert(`Error: ${error.message || 'Failed to update user'}`);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user');
    }
  };

  if (error && error.includes('Access Denied')) {
    return (
      <div className="admin-panel">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin panel.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title">
            <Shield size={32} />
            <h1>Admin Panel</h1>
          </div>
          <button onClick={() => navigate('/dashboard')} className="btn-secondary">
            <Home size={20} />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'stats' ? 'active' : ''}
          onClick={() => setActiveTab('stats')}
        >
          <BarChart3 size={20} />
          Statistics
        </button>
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          <Users size={20} />
          Users ({users.length})
        </button>
        <button
          className={activeTab === 'email' ? 'active' : ''}
          onClick={() => setActiveTab('email')}
        >
          <Mail size={20} />
          Email ({contactMessages.filter(m => !m.read_at).length})
        </button>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={20} />
          Settings
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'stats' && (
          <div className="admin-stats">
            <h2>System Statistics</h2>
            {stats ? (
              <div className="stats-grid">
                <div className="stat-card">
                  <Users size={24} />
                  <div className="stat-value">{stats.totalUsers}</div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-card">
                  <Shield size={24} />
                  <div className="stat-value">{stats.activeUsers}</div>
                  <div className="stat-label">Active Users</div>
                </div>
                <div className="stat-card">
                  <Shield size={24} />
                  <div className="stat-value">{stats.adminUsers}</div>
                  <div className="stat-label">Admin Users</div>
                </div>
                <div className="stat-card">
                  <Home size={24} />
                  <div className="stat-value">{stats.totalProperties}</div>
                  <div className="stat-label">Properties</div>
                </div>
                <div className="stat-card">
                  <DollarSign size={24} />
                  <div className="stat-value">{stats.totalExpenses}</div>
                  <div className="stat-label">Expenses</div>
                </div>
                <div className="stat-card">
                  <TrendingUp size={24} />
                  <div className="stat-value">{stats.systemStatus}</div>
                  <div className="stat-label">System Status</div>
                </div>
              </div>
            ) : (
              <p>No statistics available</p>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-users">
            <h2>User Management</h2>
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Email Verified</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      onUpdate={handleUpdateUser}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="admin-email">
            <h2>Contact Form Messages</h2>
            <p className="admin-email-desc">Messages sent via the contact form (Terms, Privacy, Legal, Help).</p>
            <div className="contact-messages-list">
              {contactMessages.length === 0 ? (
                <p className="no-messages">No contact messages yet.</p>
              ) : (
                contactMessages.map((msg) => (
                  <div key={msg.id} className={`contact-message-card ${msg.read_at ? 'read' : 'unread'}`}>
                    <div className="message-header">
                      <span className="message-from">{msg.name} &lt;{msg.email}&gt;</span>
                      <span className="message-date">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <div className="message-subject"><strong>Subject:</strong> {msg.subject}</div>
                    <div className="message-body">{msg.message}</div>
                    {!msg.read_at && (
                      <a href={`mailto:${msg.email}?subject=Re: ${encodeURIComponent(msg.subject)}`} className="btn-reply">
                        Reply
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="admin-settings">
            <h2>System Settings</h2>
            <p>Settings panel coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

const UserRow = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    role: user.role || 'member',
    is_active: user.is_active !== undefined ? user.is_active : 1
  });

  const handleSave = () => {
    onUpdate(user.id, formData);
    setIsEditing(false);
  };

  return (
    <tr>
      <td>{user.id}</td>
      <td>{user.email}</td>
      <td>{user.first_name} {user.last_name}</td>
      <td>
        {isEditing ? (
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        ) : (
          <span className={`role-badge ${user.role || 'member'}`}>
            {user.role || 'member'}
          </span>
        )}
      </td>
      <td>
        {isEditing ? (
          <select
            value={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        ) : (
          <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
            {user.is_active ? 'Active' : 'Inactive'}
          </span>
        )}
      </td>
      <td>
        <span className={`status-badge ${user.email_verified ? 'verified' : 'unverified'}`}>
          {user.email_verified ? 'Yes' : 'No'}
        </span>
      </td>
      <td>{new Date(user.created_at).toLocaleDateString()}</td>
      <td>
        {isEditing ? (
          <div className="action-buttons">
            <button onClick={handleSave} className="btn-save">Save</button>
            <button onClick={() => setIsEditing(false)} className="btn-cancel">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setIsEditing(true)} className="btn-edit">Edit</button>
        )}
      </td>
    </tr>
  );
};

export default AdminPanel;

